import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import { useDateFormat } from '../contexts/DateFormatContext';
import Layout from '../components/Layout';
import NotificationBanner from '../components/NotificationBanner';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ConfirmDialog } from '../components/ui/confirm-dialog';
import { TrendingUp, Brain, Trophy, Target, Lock, AlertTriangle, Info, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import PersonalizedPredictions from '../components/predictions/PersonalizedPredictions';
import { trackEvent } from '../components/GoogleAnalytics';
import { getSelectionLabel as getSelectionLabelRaw } from '../utils/formatters';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PredictionsAI = () => {
  const { user } = useContext(AuthContext);
  const { formatDate } = useDateFormat();
  const navigate = useNavigate();
  const [predictions, setPredictions] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [checkingResults, setCheckingResults] = useState(false);
  const [showRegenerateDialog, setShowRegenerateDialog] = useState(false);
  
  // Display settings from admin
  const [displaySettings, setDisplaySettings] = useState({
    history_start_date: null,
    history_end_date: null,
    info_text: '',
    show_info_card: true
  });
  
  // Historical predictions state
  const [singlesHistory, setSinglesHistory] = useState({ predictions: [], pagination: {} });
  const [doublesHistory, setDoublesHistory] = useState({ predictions: [], pagination: {} });
  const [treblesHistory, setTreblesHistory] = useState({ predictions: [], pagination: {} });
  const [singlesPage, setSinglesPage] = useState(1);
  const [doublesPage, setDoublesPage] = useState(1);
  const [treblesPage, setTreblesPage] = useState(1);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [singlesAnalytics, setSinglesAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  const isPremium = user?.subscription_plan && user.subscription_plan !== 'free';

  useEffect(() => {
    fetchPredictions();
    fetchDisplaySettings();
    if (isPremium) {
      fetchStatistics();
      fetchHistory('single', 1);
      fetchHistory('double', 1);
      fetchHistory('treble', 1);
      fetchSinglesAnalytics();
    }
  }, [isPremium]);

  const fetchPredictions = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/predictions-ai/today`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 120000  // 2 minute timeout for generation
      });
      console.log('Predictions API Response:', response.data);
      console.log('Singles count:', response.data?.singles?.length);
      console.log('Double:', response.data?.double ? 'Yes' : 'No');
      console.log('Treble:', response.data?.treble ? 'Yes' : 'No');
      setPredictions(response.data);
      
      // Track predictions view (only once when data loads successfully)
      if (response.data && !response.data.generating) {
        trackEvent('prediction_viewed', 'AI Predictions', 'Daily Predictions');
      }
      
      // Check if generation is in progress
      if (response.data.generating) {
        setGenerating(true);
        // Poll every 15 seconds until predictions are ready
        setTimeout(() => fetchPredictions(), 15000);
      } else {
        setGenerating(false);
      }
    } catch (error) {
      console.error('Error fetching predictions:', error);
      // Only show error if not a timeout during generation
      if (!generating) {
        toast.error('Failed to load predictions');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchDisplaySettings = async () => {
    try {
      const response = await axios.get(`${API}/predictions-ai/display-settings`);
      setDisplaySettings(response.data);
    } catch (error) {
      console.error('Error fetching display settings:', error);
    }
  };

  const formatPeriodDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const handleCheckResults = async () => {
    setCheckingResults(true);
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.post(
        `${API}/predictions-ai/check-results`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      toast.success(
        `✅ Checked results: ${response.data.updated_count} predictions updated`,
        { duration: 5000 }
      );
      
      // Refresh predictions and statistics
      fetchPredictions();
      fetchStatistics();
      fetchHistory('single', singlesPage);
      fetchHistory('double', doublesPage);
      fetchHistory('treble', treblesPage);
      
    } catch (error) {
      console.error('Error checking results:', error);
      if (error.response?.status === 403) {
        toast.error('Admin access required');
      } else {
        toast.error('Failed to check results');
      }
    } finally {
      setCheckingResults(false);
    }
  };

  const handleRegenerateClick = () => {
    setShowRegenerateDialog(true);
  };

  const handleRegenerateConfirm = async () => {
    setShowRegenerateDialog(false);
    setRegenerating(true);
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.post(
        `${API}/predictions-ai/regenerate`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      toast.success(
        `Deleted ${response.data.deleted_count} old predictions. Generating new ones...`,
        { duration: 5000 }
      );
      
      // Clear current predictions
      setPredictions(null);
      setGenerating(true);
      
      // Start polling
      setTimeout(() => fetchPredictions(), 5000);
      
    } catch (error) {
      console.error('Error regenerating predictions:', error);
      if (error.response?.status === 403) {
        toast.error('Admin access required');
      } else {
        toast.error('Failed to regenerate predictions');
      }
    } finally {
      setRegenerating(false);
    }
  };

  const fetchStatistics = async () => {
    setStatsLoading(true);
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/predictions-ai/statistics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatistics(response.data);
    } catch (error) {
      console.error('Error fetching statistics:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchHistory = async (type, page) => {
    setHistoryLoading(true);
    try {
      const token = localStorage.getItem('session_token');
      // Different limits for better alignment in desktop view
      const limit = type === 'single' ? 13 : type === 'double' ? 12 : 9;
      const response = await axios.get(`${API}/predictions-ai/history`, {
        params: { prediction_type: type, page, limit },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (type === 'single') {
        setSinglesHistory(response.data);
      } else if (type === 'double') {
        setDoublesHistory(response.data);
      } else if (type === 'treble') {
        setTreblesHistory(response.data);
      }
    } catch (error) {
      console.error(`Error fetching ${type} history:`, error);
    } finally {
      setHistoryLoading(false);
    }
  };

  const fetchSinglesAnalytics = async () => {
    setAnalyticsLoading(true);
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/predictions-ai/singles-analytics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSinglesAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching singles analytics:', error);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  // Use shared formatter - the raw utility provides clean labels
  const getSelectionLabel = (selection, homeTeam, awayTeam) => {
    return getSelectionLabelRaw(selection, homeTeam, awayTeam);
  };

  const formatMatchTime = (startTime) => {
    if (!startTime) return '';
    
    try {
      const date = new Date(startTime);
      // Format time according to user's locale
      const timeString = date.toLocaleTimeString([], { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: false 
      });
      return timeString;
    } catch (e) {
      return '';
    }
  };

  const renderPredictionCard = (prediction, index) => {
    const match = prediction.matches[0];
    const actualResult = prediction.actual_result;
    const hasResult = actualResult !== undefined && actualResult !== null;
    
    return (
      <Card key={index} className="glass-dark border-white/10">
        <CardContent className="pt-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="text-sm text-blue-400 mb-1">
                {match.country && match.league 
                  ? `${match.country} - ${match.league}` 
                  : (match.league || prediction.league || 'European League')}
              </div>
              <h3 className="text-lg font-bold text-white mb-1">
                {match.home_team} vs {match.away_team}
              </h3>
              {match.start_time && !hasResult && (
                <div className="text-xs text-gray-400 mb-1 flex items-center gap-1">
                  🕐 <span>{formatMatchTime(match.start_time)}</span>
                </div>
              )}
              {hasResult && (
                <div className="text-sm text-gray-400 mb-1">
                  Score: <span className="text-white font-semibold">{actualResult.home_score}-{actualResult.away_score}</span>
                </div>
              )}
              <div className="text-gray-300">
                <span className="font-semibold">Pick:</span> {getSelectionLabel(match.selection, match.home_team, match.away_team)}
              </div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-blue-400">{match.odds.toFixed(2)}</div>
              <div className="text-xs text-gray-400">Odds</div>
            </div>
          </div>
          
          <div className="bg-white/5 rounded-lg p-3 mb-3">
            <div className="flex items-center gap-2 mb-2">
              <Brain className="w-4 h-4 text-purple-400" />
              <span className="text-sm font-semibold text-white">AI Analysis</span>
            </div>
            <p className="text-sm text-gray-300">{prediction.ai_reasoning}</p>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Target className="w-4 h-4 text-green-400" />
              <span className="text-sm text-gray-400">Confidence:</span>
              <span className="text-sm font-semibold text-white">{prediction.confidence_score}%</span>
            </div>
            {prediction.outcome && (
              <div className={`px-3 py-1 rounded-full text-xs font-semibold ${
                prediction.outcome === 'won' ? 'bg-green-500/20 text-green-400' :
                prediction.outcome === 'lost' ? 'bg-red-500/20 text-red-400' :
                'bg-yellow-500/20 text-yellow-400'
              }`}>
                {prediction.outcome.toUpperCase()}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderAccumulatorCard = (prediction, title, accentColor = "blue") => {
    // Show "no predictions" card when prediction is not available
    if (!prediction) {
      return (
        <Card className="glass-dark border-white/10">
          <CardContent className="pt-6">
            <div className="text-center py-8 text-gray-400">
              <Trophy className={`w-12 h-12 mx-auto mb-3 ${accentColor === 'purple' ? 'text-purple-400' : 'text-blue-400'}`} />
              <p>No {title.toLowerCase()} prediction available for today.</p>
              <p className="text-sm mt-2">
                Not enough matches met the selection criteria (odds range 2.00-3.00).
              </p>
              <p className="text-xs mt-2 text-gray-500">
                Check back tomorrow at 2:00 AM UTC for new predictions!
              </p>
            </div>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card className="glass-dark border-white/10">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Trophy className="w-5 h-5 text-yellow-400" />
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 mb-4">
            {prediction.matches.map((match, idx) => {
              // Get the actual result for this match if available
              const actualResult = prediction.actual_results && prediction.actual_results[idx];
              const isCorrect = actualResult?.correct;
              const isVoid = actualResult?.correct === null && actualResult?.status;
              const hasResult = actualResult !== undefined;
              
              return (
                <div key={idx} className="bg-white/5 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex-1">
                      <div className="text-xs text-blue-400 mb-1">
                        {match.country && match.league 
                          ? `${match.country} - ${match.league}` 
                          : (match.league || 'European League')}
                      </div>
                      <div className="text-sm font-semibold text-white">
                        {match.home_team} vs {match.away_team}
                      </div>
                      {match.start_time && !hasResult && (
                        <div className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                          🕐 <span>{formatMatchTime(match.start_time)}</span>
                        </div>
                      )}
                      <div className="text-xs text-gray-400 mt-1">
                        Pick: <span className="text-white">{getSelectionLabel(match.selection, match.home_team, match.away_team)}</span>
                      </div>
                      {hasResult && actualResult && !isVoid && (
                        <div className="text-xs text-gray-500 mt-1">
                          Score: {actualResult.home_score}-{actualResult.away_score}
                        </div>
                      )}
                      {isVoid && (
                        <div className="text-xs text-yellow-500 mt-1">
                          Match Postponed/Cancelled
                        </div>
                      )}
                    </div>
                    <div className="text-right flex flex-col items-end gap-1">
                      <div className={`text-lg font-bold ${isVoid ? 'text-yellow-400 line-through' : 'text-blue-400'}`}>
                        {isVoid ? (
                          <span>
                            <span className="line-through">{match.odds.toFixed(2)}</span>
                            <span className="text-sm ml-1 no-underline">→ 1.00</span>
                          </span>
                        ) : match.odds.toFixed(2)}
                      </div>
                      {hasResult && (
                        <div className={`px-2 py-0.5 rounded text-xs font-semibold ${
                          isVoid
                            ? 'bg-yellow-500/20 text-yellow-400'
                            : isCorrect 
                              ? 'bg-green-500/20 text-green-400' 
                              : 'bg-red-500/20 text-red-400'
                        }`}>
                          {isVoid ? '⚠ VOID' : (isCorrect ? '✓ WON' : '✗ LOST')}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-lg p-4 mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-white font-semibold">Total Odds:</span>
              <div className="text-right">
                {prediction.has_void_match && prediction.adjusted_total_odds ? (
                  <div>
                    <span className="text-lg text-gray-400 line-through mr-2">{prediction.total_odds.toFixed(2)}</span>
                    <span className="text-2xl font-bold text-yellow-400">{prediction.adjusted_total_odds.toFixed(2)}</span>
                    <div className="text-xs text-yellow-400">Adjusted (void @ 1.00)</div>
                  </div>
                ) : (
                  <span className="text-2xl font-bold text-blue-400">{prediction.total_odds.toFixed(2)}</span>
                )}
              </div>
            </div>
          </div>

          <div className="bg-white/5 rounded-lg p-3 mb-3">
            <div className="flex items-center gap-2 mb-2">
              <Brain className="w-4 h-4 text-purple-400" />
              <span className="text-sm font-semibold text-white">AI Analysis</span>
            </div>
            <p className="text-sm text-gray-300">{prediction.ai_reasoning}</p>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Target className="w-4 h-4 text-green-400" />
              <span className="text-sm text-gray-400">Confidence:</span>
              <span className="text-sm font-semibold text-white">{prediction.confidence_score}%</span>
            </div>
            {prediction.outcome && (
              <div className={`px-3 py-1 rounded-full text-xs font-semibold ${
                prediction.outcome === 'won' ? 'bg-green-500/20 text-green-400' :
                prediction.outcome === 'lost' ? 'bg-red-500/20 text-red-400' :
                'bg-yellow-500/20 text-yellow-400'
              }`}>
                {prediction.outcome.toUpperCase()}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderStatistics = () => {
    if (!isPremium) {
      return (
        <Card className="glass-dark border-yellow-500/30 overflow-hidden relative">
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-yellow-500 via-orange-500 to-yellow-500" />
          <CardContent className="pt-6">
            <div className="text-center py-8">
              <Lock className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">Premium Features</h3>
              <p className="text-gray-300 mb-4 max-w-lg mx-auto">
                Unlock powerful AI prediction tools designed to help you make smarter betting decisions.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 text-left max-w-2xl mx-auto">
                <div className="p-4 rounded-lg bg-purple-500/10 border border-purple-500/20">
                  <h4 className="text-purple-400 font-semibold mb-2 flex items-center gap-2">
                    <Brain className="w-4 h-4" />
                    Personalized Predictions
                  </h4>
                  <p className="text-gray-400 text-sm">
                    Generate custom AI predictions based on your preferred odds range, markets, and leagues.
                  </p>
                </div>
                <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/20">
                  <h4 className="text-blue-400 font-semibold mb-2 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" />
                    Performance Statistics
                  </h4>
                  <p className="text-gray-400 text-sm">
                    Track AI prediction performance over time with detailed win rates, ROI, and historical data.
                  </p>
                </div>
              </div>
              
              <div className="flex flex-wrap justify-center gap-2 mb-6">
                <span className="px-3 py-1 rounded-full text-xs bg-purple-500/20 text-purple-400 border border-purple-500/30">
                  Custom Odds Range
                </span>
                <span className="px-3 py-1 rounded-full text-xs bg-blue-500/20 text-blue-400 border border-blue-500/30">
                  Market Selection
                </span>
                <span className="px-3 py-1 rounded-full text-xs bg-green-500/20 text-green-400 border border-green-500/30">
                  League Filtering
                </span>
                <span className="px-3 py-1 rounded-full text-xs bg-orange-500/20 text-orange-400 border border-orange-500/30">
                  Win Rate Analytics
                </span>
              </div>
              
              <Button
                onClick={() => navigate('/subscription')}
                className="bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-white"
              >
                Upgrade to Premium
              </Button>
            </div>
          </CardContent>
        </Card>
      );
    }

    if (statsLoading) {
      return (
        <Card className="glass-dark border-white/10">
          <CardContent className="pt-6">
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400 mx-auto"></div>
              <p className="text-gray-400 mt-4">Loading statistics...</p>
            </div>
          </CardContent>
        </Card>
      );
    }

    if (!statistics) return null;

    const renderStatsCard = (title, data, icon) => (
      <Card className="glass-dark border-white/10">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2 text-lg">
            {icon}
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-2xl font-bold text-white">{data.total_predictions}</div>
              <div className="text-xs text-gray-400">Total Predictions</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-green-400">{data.win_rate}%</div>
              <div className="text-xs text-gray-400">Win Rate</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-blue-400">{data.roi}%</div>
              <div className="text-xs text-gray-400">ROI</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-purple-400">{data.avg_odds}</div>
              <div className="text-xs text-gray-400">Avg Odds</div>
            </div>
          </div>
          <div className="mt-4 flex items-center justify-around text-sm">
            <div className="text-center">
              <div className="text-green-400 font-semibold">{data.won}</div>
              <div className="text-gray-400 text-xs">Won</div>
            </div>
            <div className="text-center">
              <div className="text-red-400 font-semibold">{data.lost}</div>
              <div className="text-gray-400 text-xs">Lost</div>
            </div>
            <div className="text-center">
              <div className="text-yellow-400 font-semibold">{data.void || 0}</div>
              <div className="text-gray-400 text-xs">Void</div>
            </div>
            <div className="text-center">
              <div className="text-gray-400 font-semibold">{data.pending}</div>
              <div className="text-gray-400 text-xs">Pending</div>
            </div>
          </div>
        </CardContent>
      </Card>
    );

    return (
      <div className="space-y-6">
        <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
          <TrendingUp className="w-6 h-6 text-blue-400" />
          Prediction Performance
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {renderStatsCard("Singles", statistics.singles, <Target className="w-5 h-5 text-green-400" />)}
          {renderStatsCard("Doubles", statistics.double, <Trophy className="w-5 h-5 text-blue-400" />)}
          {renderStatsCard("Trebles", statistics.treble, <Trophy className="w-5 h-5 text-purple-400" />)}
        </div>
        
        {/* Singles Analytics */}
        {singlesAnalytics && (singlesAnalytics.top_leagues?.length > 0 || singlesAnalytics.top_markets?.length > 0) && (
          <div className="mt-8">
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2" data-testid="singles-analytics-heading">
              <Sparkles className="w-6 h-6 text-yellow-400" />
              Singles Analytics
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Top Profitable Leagues */}
              {singlesAnalytics.top_leagues?.length > 0 && (
                <Card className="glass-dark border-white/10" data-testid="top-leagues-card">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-white text-base flex items-center gap-2">
                      <Trophy className="w-4 h-4 text-yellow-400" />
                      Top Profitable Leagues
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="space-y-0">
                      <div className="grid grid-cols-12 text-xs text-gray-500 pb-2 border-b border-white/5 px-1">
                        <div className="col-span-4">League</div>
                        <div className="col-span-2 text-center">Avg Odds</div>
                        <div className="col-span-2 text-center">Bets</div>
                        <div className="col-span-2 text-center">Win %</div>
                        <div className="col-span-2 text-right">ROI</div>
                      </div>
                      {singlesAnalytics.top_leagues.map((league, i) => (
                        <div key={i} className="grid grid-cols-12 items-center py-2 border-b border-white/5 text-sm px-1" data-testid={`league-row-${i}`}>
                          <div className="col-span-4 text-gray-200 truncate pr-2" title={league.name}>{league.name}</div>
                          <div className="col-span-2 text-center text-gray-400">{league.avg_odds}</div>
                          <div className="col-span-2 text-center text-gray-400">{league.total}</div>
                          <div className="col-span-2 text-center text-gray-300">{league.win_rate}%</div>
                          <div className={`col-span-2 text-right font-semibold ${league.roi > 0 ? 'text-green-400' : league.roi === 0 ? 'text-gray-400' : 'text-red-400'}`}>
                            {league.roi > 0 ? '+' : ''}{league.roi}%
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Top Profitable Markets */}
              {singlesAnalytics.top_markets?.length > 0 && (
                <Card className="glass-dark border-white/10" data-testid="top-markets-card">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-white text-base flex items-center gap-2">
                      <Target className="w-4 h-4 text-green-400" />
                      Top Profitable Markets
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="space-y-0">
                      <div className="grid grid-cols-12 text-xs text-gray-500 pb-2 border-b border-white/5 px-1">
                        <div className="col-span-4">Market</div>
                        <div className="col-span-2 text-center">Avg Odds</div>
                        <div className="col-span-2 text-center">Bets</div>
                        <div className="col-span-2 text-center">Win %</div>
                        <div className="col-span-2 text-right">ROI</div>
                      </div>
                      {singlesAnalytics.top_markets.map((market, i) => (
                        <div key={i} className="grid grid-cols-12 items-center py-2 border-b border-white/5 text-sm px-1" data-testid={`market-row-${i}`}>
                          <div className="col-span-4 text-gray-200 truncate pr-2" title={getSelectionLabel(market.name)}>{getSelectionLabel(market.name)}</div>
                          <div className="col-span-2 text-center text-gray-400">{market.avg_odds}</div>
                          <div className="col-span-2 text-center text-gray-400">{market.total}</div>
                          <div className="col-span-2 text-center text-gray-300">{market.win_rate}%</div>
                          <div className={`col-span-2 text-right font-semibold ${market.roi > 0 ? 'text-green-400' : market.roi === 0 ? 'text-gray-400' : 'text-red-400'}`}>
                            {market.roi > 0 ? '+' : ''}{market.roi}%
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        )}

        {analyticsLoading && !singlesAnalytics && (
          <div className="mt-8 text-center text-gray-500 text-sm">Loading analytics...</div>
        )}
        
        {/* Historical Predictions */}
        <div className="mt-8">
          <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
            <Info className="w-6 h-6 text-purple-400" />
            Recent Predictions History
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {renderHistoryCard("Singles", singlesHistory, singlesPage, (page) => {
              setSinglesPage(page);
              fetchHistory('single', page);
            }, <Target className="w-5 h-5 text-green-400" />)}
            {renderHistoryCard("Doubles", doublesHistory, doublesPage, (page) => {
              setDoublesPage(page);
              fetchHistory('double', page);
            }, <Trophy className="w-5 h-5 text-blue-400" />)}
            {renderHistoryCard("Trebles", treblesHistory, treblesPage, (page) => {
              setTreblesPage(page);
              fetchHistory('treble', page);
            }, <Trophy className="w-5 h-5 text-purple-400" />)}
          </div>
        </div>
      </div>
    );
  };

  const renderHistoryCard = (title, historyData, currentPage, onPageChange, icon) => {
    const { predictions, pagination } = historyData;
    
    return (
      <Card className="glass-dark border-white/10">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2 text-lg">
            {icon}
            {title} History
          </CardTitle>
        </CardHeader>
        <CardContent>
          {historyLoading && predictions.length === 0 ? (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400 mx-auto"></div>
            </div>
          ) : predictions.length === 0 ? (
            <div className="text-center py-4 text-gray-400">
              No history available yet
            </div>
          ) : (
            <div className="space-y-2">
              {predictions.map((pred, index) => (
                <div key={pred.id || index} className="bg-white/5 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex-1">
                      <div className="text-sm text-gray-400">
                        {formatDate(pred.date)}
                      </div>
                      <div className="text-xs text-gray-500">{pred.league}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-bold">
                        {pred.has_void_match && pred.adjusted_total_odds ? (
                          <span className="text-yellow-400">{pred.adjusted_total_odds.toFixed(2)}</span>
                        ) : (
                          <span className="text-blue-400">{pred.total_odds?.toFixed(2)}</span>
                        )}
                      </div>
                      {pred.outcome === 'won' ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-green-500/20 text-green-400">
                          WON
                        </span>
                      ) : pred.outcome === 'void' ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-yellow-500/20 text-yellow-400">
                          VOID
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-red-500/20 text-red-400">
                          LOST
                        </span>
                      )}
                    </div>
                  </div>
                  {pred.matches && pred.matches.length > 0 && (
                    <div className="text-xs text-gray-300 mt-1 space-y-1">
                      {pred.matches.map((match, idx) => {
                        // Get the actual result for this match if available
                        // Singles use actual_result (singular), doubles/trebles use actual_results (plural array)
                        let actualResult, isCorrect, hasResult, isVoid;
                        
                        if (pred.matches.length === 1 && pred.actual_result) {
                          // Single prediction
                          actualResult = pred.actual_result;
                          hasResult = true;
                          isCorrect = pred.outcome === 'won';
                          isVoid = pred.outcome === 'void';
                        } else if (pred.actual_results && pred.actual_results[idx]) {
                          // Double/Treble prediction
                          actualResult = pred.actual_results[idx];
                          isCorrect = actualResult?.correct;
                          isVoid = actualResult?.correct === null && actualResult?.status;
                          hasResult = actualResult !== undefined;
                        } else {
                          hasResult = false;
                          isVoid = false;
                        }
                        
                        return (
                          <div key={idx}>
                            {/* Show country - league for singles in history */}
                            {pred.matches.length === 1 && (
                              <div className="text-[10px] text-blue-400 mb-0.5">
                                {match.country && match.league 
                                  ? `${match.country} - ${match.league}` 
                                  : (match.league || pred.league || '')}
                              </div>
                            )}
                            <div className="truncate font-medium">
                              {match.home_team} vs {match.away_team}
                              {hasResult && actualResult && !isVoid && (
                                <span className="text-gray-500 ml-1">
                                  ({actualResult.home_score}-{actualResult.away_score})
                                </span>
                              )}
                              {isVoid && (
                                <span className="text-yellow-500 ml-1">
                                  (Postponed)
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-blue-300 flex items-center justify-between gap-1">
                              <div className="flex items-center gap-1">
                                <span className="font-semibold">
                                  {getSelectionLabel(match.selection, match.home_team, match.away_team)}
                                </span>
                                <span className={isVoid ? 'text-yellow-400' : 'text-gray-400'}>
                                  {isVoid ? (
                                    <span><span className="line-through">@{match.odds}</span> → @1.00</span>
                                  ) : (
                                    `@${match.odds}`
                                  )}
                                </span>
                              </div>
                              {hasResult && (
                                <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                                  isVoid
                                    ? 'bg-yellow-500/20 text-yellow-400'
                                    : isCorrect 
                                      ? 'bg-green-500/20 text-green-400' 
                                      : 'bg-red-500/20 text-red-400'
                                }`}>
                                  {isVoid ? '−' : (isCorrect ? '✓' : '✗')}
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          
          {/* Pagination */}
          {pagination.total_pages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/10">
              <Button
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
                size="sm"
                variant="outline"
                className="border-white/10 text-white hover:bg-white/10"
              >
                Previous
              </Button>
              <span className="text-sm text-gray-400">
                Page {currentPage} of {pagination.total_pages}
              </span>
              <Button
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === pagination.total_pages}
                size="sm"
                variant="outline"
                className="border-white/10 text-white hover:bg-white/10"
              >
                Next
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 p-4 sm:p-6 lg:p-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center py-20">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-400 mx-auto"></div>
              <p className="text-gray-400 mt-4">Loading AI predictions...</p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 p-4 sm:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto">
          {/* Notification Banner */}
          <NotificationBanner type="predictions" />

          {/* Header */}
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
              <h1 className="text-3xl sm:text-4xl font-bold text-white flex items-center gap-3">
                <Brain className="w-10 h-10 text-blue-400" />
                AI Predictions
              </h1>
              
              {/* Admin Buttons */}
              {user?.is_admin && (
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button
                    onClick={handleCheckResults}
                    disabled={checkingResults || regenerating || generating}
                    className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white shadow-lg text-sm sm:text-base"
                  >
                    {checkingResults ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                        Checking...
                      </>
                    ) : (
                      <>
                        ✓ Check Results Now
                      </>
                    )}
                  </Button>
                  <Button
                    onClick={handleRegenerateClick}
                    disabled={regenerating || generating || checkingResults}
                    className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white shadow-lg text-sm sm:text-base"
                  >
                    {regenerating ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                        Regenerating...
                      </>
                    ) : (
                      <>
                        🔄 Regenerate
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
            <p className="text-gray-300 text-lg">
              Daily AI-powered betting predictions for European leagues, international matches, and UEFA competitions
            </p>
          </div>

          {/* Disclaimer */}
          <Card className="glass-dark border-yellow-500/30 mb-6">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-yellow-400 mb-2">Important Disclaimer</h3>
                  <p className="text-sm text-gray-300 mb-2">
                    These predictions are for entertainment purposes only. AI predictions are based on statistical analysis
                    but cannot guarantee outcomes. Only bet what you can afford to lose.
                  </p>
                  <p className="text-xs text-gray-400">
                    18+ only. Please gamble responsibly. Visit <a href="https://www.begambleaware.org" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">BeGambleAware.org</a> for support.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Generation Status */}
          {generating && (
            <Card className="glass-dark border-blue-500/30 mb-6">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
                  <div>
                    <h3 className="font-semibold text-blue-400">Generating Predictions...</h3>
                    <p className="text-sm text-gray-300">
                      AI is analyzing today's matches. This may take 1-2 minutes. Page will auto-refresh.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Predictions Date */}
          {predictions && !generating && (
            <div className="mb-6 flex items-center justify-between">
              <div className="flex items-center gap-2 text-gray-300">
                <Info className="w-4 h-4" />
                <span>Predictions for: <span className="font-semibold text-white">{formatDate(predictions.date)}</span></span>
              </div>
              <div className="text-sm text-gray-400">
                Generated: {formatDate(predictions.generated_at, true)}
              </div>
            </div>
          )}

          {/* Singles */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
              <Target className="w-6 h-6 text-green-400" />
              Single Bets (Odds 1.30 - 2.00)
            </h2>
            {predictions?.singles && predictions.singles.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {predictions.singles.map((prediction, idx) => renderPredictionCard(prediction, idx))}
              </div>
            ) : (
              <Card className="glass-dark border-white/10">
                <CardContent className="pt-6">
                  <div className="text-center py-8 text-gray-400">
                    <p>No single bet predictions available yet.</p>
                    <p className="text-sm mt-2">Check back tomorrow at 2:00 AM UTC for new predictions!</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Double Accumulator */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
              <Trophy className="w-6 h-6 text-blue-400" />
              Double Accumulator (Total Odds 1.70 - 2.25)
            </h2>
            {renderAccumulatorCard(predictions?.double, "2-Fold Accumulator", "blue")}
          </div>

          {/* Treble Accumulator */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
              <Trophy className="w-6 h-6 text-purple-400" />
              Treble Accumulator (Total Odds 2.00 - 3.00)
            </h2>
            {renderAccumulatorCard(predictions?.treble, "3-Fold Accumulator", "purple")}
          </div>

          {/* Personalized Predictions Section */}
          <div className="mt-12">
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-purple-400" />
              Personalized AI Predictions
            </h2>
            <PersonalizedPredictions />
          </div>

          {/* Info Card - Period and custom text for premium users */}
          {isPremium && displaySettings.show_info_card && (displaySettings.info_text || displaySettings.history_start_date || displaySettings.history_end_date) && (
            <Card className="glass-dark border-blue-500/30 mt-8">
              <CardContent className="pt-4 pb-4">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                  <div>
                    {(displaySettings.history_start_date || displaySettings.history_end_date) && (
                      <p className="text-sm text-blue-400 mb-1">
                        <span className="font-medium">Statistics Period: </span>
                        {formatPeriodDate(displaySettings.history_start_date) || 'Beginning'} 
                        {' → '} 
                        {formatPeriodDate(displaySettings.history_end_date) || 'Present'}
                      </p>
                    )}
                    {displaySettings.info_text && (
                      <p className="text-gray-300 text-sm">{displaySettings.info_text}</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Statistics Section */}
          <div className="mt-8">
            {renderStatistics()}
          </div>
        </div>
      </div>

      {/* Regenerate Confirmation Dialog */}
      <ConfirmDialog
        open={showRegenerateDialog}
        onOpenChange={setShowRegenerateDialog}
        title="⚠️ Regenerate Predictions"
        description="This will delete today's predictions and generate new ones. New predictions will use your synced leagues and prioritize commercial matches. Generation takes 1-2 minutes. Continue?"
        onConfirm={handleRegenerateConfirm}
        confirmText="Yes, Regenerate"
        cancelText="Cancel"
        variant="destructive"
      />
    </Layout>
  );
};

export default PredictionsAI;
